import {
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {get} from 'lodash';
import {FC, useEffect} from 'react';
import Chart from 'react-apexcharts';
import {useStore} from 'zustand';
import {STATUS_LABELS} from '../../constants/status';
import {useSecondaryChartDataGate} from '../../hooks/useSecondaryChartDataGate';
import {useStatusOptions} from '../../hooks/useStausOptions';
import {useStoresContext} from '../../hooks/useStoresContext';
import {useSecondaryChartsDataStore} from '../../store/useSecondaryChartsDataStore';
import {FallbackWithChildren} from '../FallbackWithChildren';
import {preparePrimaryChartData, prepareSecondaryChartData} from './helpers';
import {STYLES} from './styles';

const renderStatusSelectLoader = (isLoading: boolean) => {
  if (!isLoading) return;
  return () => <Stack width={32} children={<CircularProgress size={12} />} />;
};

interface Props {
  serviceType: string;
}

export const SecondaryCharts: FC<Props> = ({serviceType}) => {
  useSecondaryChartDataGate(serviceType);

  const {secondaryChartsFiltersStore: sS, primaryChartsFiltersStore} = useStoresContext(serviceType);
  const contracts = useSecondaryChartsDataStore((s) => s.contracts);
  const isLoading = useSecondaryChartsDataStore((s) => s.isLoading);

  const {period, customPeriodEnd, customPeriodStart} = useStore(primaryChartsFiltersStore);

  const {status, setStatus, resetStatus} = useStore(sS);

  const useStatusOptionsProps = {serviceType, period, from: customPeriodStart, to: customPeriodEnd};
  const {statusOptions, loading} = useStatusOptions(useStatusOptionsProps);
  const isStatusOptionsLoading = isLoading || loading;

  const primary = preparePrimaryChartData(contracts);
  const secondary = prepareSecondaryChartData(contracts);

  useEffect(() => {
    resetStatus();
  }, [period, resetStatus]);

  const handleRequestStatusChange = (e: SelectChangeEvent) => {
    setStatus(e.target.value);
  };

  return (
    <Stack height="100%">
      <Stack height="100%">
        <Stack direction="row" gap="13px">
          <Tooltip placement="top" title={get(STATUS_LABELS, status)}>
            <FormControl sx={{minWidth: '58%'}} size="small">
              <InputLabel id="request-status" sx={STYLES.chartSelectLabel}>
                Статус заявки
              </InputLabel>
              <Select
                labelId="request-status"
                id="request-status-select"
                label="Статус заявки"
                sx={STYLES.chartSelect}
                value={status}
                onChange={handleRequestStatusChange}
                disabled={isStatusOptionsLoading}
                IconComponent={renderStatusSelectLoader(isStatusOptionsLoading)}
              >
                {statusOptions.map(({label, value}) => (
                  <MenuItem key={`secondary_${value}`} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Tooltip>
        </Stack>
        <Stack position="relative" height="100%">
          <FallbackWithChildren isLoading={isLoading}>
            <Stack position="relative">
              <Chart options={primary.options} series={primary.series} type="bar" height={230} />
            </Stack>
            <Stack mt="auto">
              <Typography sx={STYLES.chartTitle}>Показатели времени завершения заявок (дней)</Typography>
              <Stack sx={STYLES.chartContainer}>
                <Chart options={secondary.options} series={secondary.series} type="bar" height={135} />
              </Stack>
            </Stack>
          </FallbackWithChildren>
        </Stack>
      </Stack>
    </Stack>
  );
};
